import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Data } from '@angular/router';
import { ISbRichtext, ISbStoryData } from '@storyblok/js';
import { copy } from '../lib/copy';
import { getAllRouteData } from '../lib/get-all-route-data';
import { StoryblokImage, StoryblokLink } from '../storyblok/storyblok-types';
import { GenericReplaceService } from './generic-replace.service';

export type StoryblokReplacer = (text: string) => string;

@Injectable({
  providedIn: 'root',
})
export class StoryblokReplaceService {
  private route = inject(ActivatedRoute);
  private genericReplacerService = inject(GenericReplaceService);

  private replacerFactories: ((route: ActivatedRouteSnapshot, routeData: Data) => StoryblokReplacer)[] = [
    this.genericReplacerService.replaceFactory.bind(this.genericReplacerService),
  ];
  private getFromKeyFactories: ((route: ActivatedRouteSnapshot, routeData: Data) => (key: string) => unknown)[] = [
    this.genericReplacerService.getFromKeyFactory.bind(this.genericReplacerService),
  ];

  public addReplacerFactory(factory: () => StoryblokReplacer) {
    this.replacerFactories.push(factory);
  }

  public addFromKeyFactory(factory: () => (key: string) => unknown) {
    this.getFromKeyFactories.push(factory);
  }

  private replace(text: string, replacers: StoryblokReplacer[]) {
    if (!text) return text;
    replacers.forEach(replacer => (text = replacer(text)));
    return text;
  }

  private richTextReplaceMutate(richText: ISbRichtext, replacers: StoryblokReplacer[]) {
    if (!richText?.content) return;
    richText.content.forEach(blok => {
      if (blok.text) blok.text = this.replace(blok.text, replacers);
      if (blok.content) this.richTextReplaceMutate(blok, replacers);
    });
  }

  private replaceInImageMutate(image: StoryblokImage, replacers: StoryblokReplacer[]) {
    if (!image) return;
    const alt = image.alt || (image.filename.includes('}}') ? image.filename.replace('}}', '__ALT}}') : undefined);
    image.alt = this.replace(alt, replacers);
    image.filename = this.replace(image.filename, replacers);
    if (image.filename.includes('img.clerk.com')) {
      image.filename = undefined;
    }
  }

  private replaceInLinkMutate(link: StoryblokLink, replacers: StoryblokReplacer[]) {
    if (!link) return;
    link.anchor = this.replace(link.anchor, replacers);
    link.url = this.replace(link.url, replacers);
    link.cached_url = this.replace(link.cached_url, replacers);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private replaceAllIn(blocks: { component: string; [key: string]: any }[], replacers: StoryblokReplacer[]) {
    blocks.forEach(block => {
      if (block.component === 'HeroVideo' || block.component === 'HomeTeaser') {
        this.replaceAllIn(block.content, replacers);
      } else if (block.component === 'TwoColumn') {
        this.replaceAllIn(block.left, replacers);
        this.replaceAllIn(block.right, replacers);
      } else if (block.component === 'ContentGroup') {
        this.replaceAllIn(block.blocks, replacers);
      } else if (block.component === 'Blur') {
        this.replaceAllIn(block.body, replacers);
      } else if (block.component === 'Teaser') {
        this.replaceAllIn(block.content, replacers);
        this.replaceInImageMutate(block.asset[0]?.image, replacers);
        this.replaceInImageMutate(block.asset[0]?.poster, replacers);
        this.replaceInImageMutate(block.asset[0]?.video, replacers);
        this.replaceInImageMutate(block.assetBackground, replacers);
      } else if (block.component === 'Title') {
        block.title = this.replace(block.title, replacers);
        this.richTextReplaceMutate(block.titleText, replacers);
      } else if (block.component === 'Button') {
        block.text = this.replace(block.text, replacers);
        this.replaceInLinkMutate(block.link, replacers);
      } else if (block.component === 'ButtonList') {
        block.buttons.forEach(button => {
          if (button.component === 'ButtonListItem') {
            button.text = this.replace(button.text, replacers);
            this.replaceInLinkMutate(button.link, replacers);
          }
          if (button.component === 'ButtonListDropdownItem') {
            [...button.button, ...button.dropdownButtons].forEach(button => {
              button.text = this.replace(button.text, replacers);
              this.replaceInLinkMutate(button.link, replacers);
            });
          }
        });
      } else if (block.component === 'CheckList') {
        block.text = this.replace(block.text, replacers);
      } else if (block.component === 'Claim') {
        block.text = this.replace(block.text, replacers);
        this.replaceInImageMutate(block.image, replacers);
      } else if (block.component === 'FaqList') {
        block.faqs.forEach(faq => {
          faq.question = this.replace(faq.question, replacers);
          this.richTextReplaceMutate(faq.answer, replacers);
        });
      } else if (block.component === 'FeatureItemList') {
        block.items.forEach(item => {
          item.title = this.replace(item.title, replacers);
          item.text = this.replace(item.text, replacers);
          this.replaceInImageMutate(item.image, replacers);
        });
      } else if (block.component === 'Image') {
        this.replaceInImageMutate(block.image, replacers);
        this.replaceInImageMutate(block.mobileImage, replacers);
      } else if (block.component === 'Text') {
        this.richTextReplaceMutate(block.content, replacers);
      } else if (block.component === 'Coupon') {
        this.replaceInImageMutate(block.image, replacers);
        this.replaceInLinkMutate(block.link, replacers);
        block.text = this.replace(block.text, replacers);
      } else if (block.component === 'Toast') {
        this.replaceInImageMutate(block.image, replacers);
        this.replaceInImageMutate(block.backgroundImage, replacers);
        this.replaceInLinkMutate(block.buttonLink, replacers);
        block.buttonText = this.replace(block.buttonText, replacers);
        this.richTextReplaceMutate(block.text, replacers);
      } else if (block.component === 'PublishedDate') {
        block.date = this.replace(block.date, replacers);
      }
    });
  }

  private getReplacers(route: ActivatedRouteSnapshot, routeData: Data) {
    return this.replacerFactories.map(factory => factory(route, routeData)).filter(Boolean);
  }

  private getFromKeyers(route: ActivatedRouteSnapshot, routeData: Data) {
    return this.getFromKeyFactories.map(factory => factory(route, routeData)).filter(Boolean);
  }

  public replaceStoryValues(story: ISbStoryData, route = this.route.snapshot, routeData?: Data): ISbStoryData {
    story = copy(story);
    if (!routeData) routeData = getAllRouteData(route);
    // Replacing values in the story for metadata

    const replacers = this.getReplacers(route, routeData);
    this.replaceAllIn(story.content.body, replacers);

    const correctedStory = <ISbStoryData>{
      ...story,
      content: {
        ...story.content,
        metaTitle: this.replace(story.content.metaTitle, replacers),
        metaDescription: this.replace(story.content.metaDescription, replacers),
        keyVisual: story.content.keyVisual
          ? {
              ...story.content.keyVisual,
              filename: this.replace((story.content.keyVisual as StoryblokImage).filename, replacers),
            }
          : story.content.keyVisual,
        ratingName: this.replace(story.content.ratingName, replacers),
      },
    };
    return correctedStory;
  }

  public replaceText(text: string) {
    const replacers = this.getReplacers(this.route.snapshot, getAllRouteData(this.route.snapshot));
    return this.replace(text, replacers);
  }

  public getFromKey(key: string, route: ActivatedRouteSnapshot = this.route.snapshot, data?: Data) {
    if (!data) data = getAllRouteData(route);
    const fromKeyers = this.getFromKeyers(route, data);

    const keyParts = key.split('.');

    for (const fromKeyer of fromKeyers) {
      let value = fromKeyer(keyParts[0]);

      if (keyParts.length > 1) {
        value = value?.['content'];
      }

      if (!value) continue;

      for (let i = 1; i < keyParts.length; i++) {
        value = value[keyParts[i]];
      }

      if (value) return value;
    }
  }
}
